import React, { useState } from "react";
import {
  Box,
  Button,
  InputAdornment,
  TextField,
  FormControlLabel,
  Checkbox,
  MenuItem,
  useTheme,
} from "@mui/material";
import { Search as SearchIcon, Add as AddIcon } from "@mui/icons-material";
import { LocationIcon } from "../../../components/icons";
import { useNavigate } from "react-router-dom";
import { tokens } from "../../../theme";
import type { WebsiteFilter } from "../types/venues.types";

interface VenueSearchBarProps {
  searchTerm: string;
  onSearchChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onSearchKeyDown: (e: React.KeyboardEvent<HTMLInputElement>) => void;
  onAddVenue: () => void;
  canAddVenue: boolean;
  primaryColor: string;
  backgroundColor?: string;
  onTogglePrivateResidence: (enabled: boolean) => void;
  onToggleCompleteness: (enabled: boolean) => void;
  websiteFilter: WebsiteFilter;
  onWebsiteFilterChange: (filter: WebsiteFilter) => void;
}

export const VenueSearchBar: React.FC<VenueSearchBarProps> = ({
  searchTerm,
  onSearchChange,
  onSearchKeyDown,
  onAddVenue,
  canAddVenue,
  primaryColor,
  backgroundColor: backgroundColorProp,
  onTogglePrivateResidence,
  onToggleCompleteness,
  websiteFilter,
  onWebsiteFilterChange,
}) => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const backgroundColor = backgroundColorProp ?? colors.primary[900];
  const navigate = useNavigate();

  const [privateOnly, setPrivateOnly] = useState(false);
  const [completenesScore, setCompletenessScore] = useState(false);

  return (
    <Box
      display="flex"
      gap={2}
      mb={4}
      alignItems="center"
      flexWrap="wrap"
      justifyContent="space-between"
    >
      <Box sx={{ display: "flex" }}>
        {/*LEFT FILTER */}
        <FormControlLabel
          control={
            <Checkbox
              checked={privateOnly}
              onChange={(e) => {
                const checked = e.target.checked;
                setPrivateOnly(checked);
                onTogglePrivateResidence(checked);
              }}
            />
          }
          label="Private Residences"
        />
        <FormControlLabel
          control={
            <Checkbox
              checked={completenesScore}
              onChange={(e) => {
                const checked = e.target.checked;
                setCompletenessScore(checked);
                onToggleCompleteness(checked);
              }}
            />
          }
          label="Completeness score < 50"
        />
        <TextField
          select
          size="small"
          label="Website"
          value={websiteFilter}
          onChange={(e) => onWebsiteFilterChange(e.target.value as WebsiteFilter)}
          sx={{ minWidth: 170, backgroundColor }}
        >
          <MenuItem value="">All venues</MenuItem>
          <MenuItem value="live">On the website</MenuItem>
          <MenuItem value="not_live">Not on the website</MenuItem>
        </TextField>
      </Box>

      {/*SEARCH */}
      <TextField
        placeholder="Search by venue name, type, or address..."
        variant="outlined"
        size="small"
        value={searchTerm}
        onChange={onSearchChange}
        onKeyDown={onSearchKeyDown}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <SearchIcon color="action" />
            </InputAdornment>
          ),
        }}
        sx={{
          minWidth: 400,
          flexGrow: 1,
          maxWidth: 300,
          backgroundColor,
        }}
      />

      {/*ACTION BUTTONS */}
      <Box>
        <Button
          variant="contained"
          startIcon={<LocationIcon />}
          disabled
          onClick={() => navigate("/site-visit")}
          sx={{
            backgroundColor: primaryColor,
            mr: 2,
          }}
        >
          Allocate Site Visit
        </Button>

        {canAddVenue && (
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={onAddVenue}
            sx={{
              backgroundColor: primaryColor,
            }}
          >
            Add Venue
          </Button>
        )}
      </Box>
    </Box>
  );
};

export default VenueSearchBar;
